import { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import { Toaster, toast } from "react-hot-toast";
import { jwtDecode } from "jwt-decode";

import * as services from "../../services/services";

function getStoredToken(key) {
  const value = localStorage.getItem(key);
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch (err) {
    return value;
  }
}

function getCurrentUser() {
  try {
    const token = getStoredToken("accessToken");
    return token ? jwtDecode(token) : null;
  } catch (err) {
    return null;
  }
}

const emptyHospital = {
  name: "",
  address: "",
  phoneNumber: "",
  subDistrict: "",
  district: "",
  province: "",
  image: "",
  imageFile: null,
};

export default function MyHospitalEditForm({ currentUser }) {
  const authUser = useMemo(() => currentUser || getCurrentUser(), [currentUser]);
  const userId = authUser?.id;

  const [hospital, setHospital] = useState(emptyHospital);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const fetchHospital = async () => {
      if (!userId) {
        toast.error("ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await services.getHospitalByUser(userId);

        if (!active) return;

        if (res.data.success) {
          const data = res.data.data || {};
          setHospital({ ...emptyHospital, ...data, imageFile: null });
          setImagePreview(data.image || null);
        } else {
          toast.error(res.data.error || "โหลดข้อมูลโรงพยาบาลไม่สำเร็จ");
        }
      } catch (err) {
        if (active) toast.error("โหลดข้อมูลโรงพยาบาลไม่สำเร็จ");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchHospital();

    return () => {
      active = false;
    };
  }, [userId]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image" && files?.[0]) {
      const file = files[0];
      setImagePreview(URL.createObjectURL(file));
      setHospital((prev) => ({ ...prev, imageFile: file }));
      return;
    }

    setHospital((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!userId) {
      toast.error("ไม่พบข้อมูลผู้ใช้ กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!hospital.name?.trim()) {
      toast.error("กรุณากรอกชื่อโรงพยาบาล");
      return;
    }

    try {
      setSaving(true);
      let imageUrl = hospital.image || "";

      if (hospital.imageFile) {
        const uploadRes = await services.uploadImage(
          hospital.imageFile,
          "hospital",
        );
        imageUrl = uploadRes.data.url;
      }

      await services.updateHospitalByUser(userId, {
        name: hospital.name,
        address: hospital.address,
        phoneNumber: hospital.phoneNumber,
        image: imageUrl,
        subDistrict: hospital.subDistrict,
        district: hospital.district,
        province: hospital.province,
      });

      setHospital((prev) => ({ ...prev, image: imageUrl, imageFile: null }));
      setImagePreview(imageUrl || null);
      toast.success("บันทึกข้อมูลโรงพยาบาลสำเร็จ");
    } catch (err) {
      toast.error(err?.response?.data?.error || "บันทึกข้อมูลโรงพยาบาลไม่สำเร็จ");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 3 }}>
      <Toaster position="bottom-left" />

      <Typography variant="h5" fontWeight={600} mb={3}>
        แก้ไขข้อมูลโรงพยาบาล
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
          gap: 4,
        }}
      >
        <Box
          sx={{
            border: "2px dashed #ddd",
            borderRadius: 3,
            p: 2,
            textAlign: "center",
            cursor: "pointer",
            minHeight: 250,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fafafa",
          }}
          onClick={() => document.getElementById("hospital-image")?.click()}
        >
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="preview"
              style={{
                width: 160,
                height: 160,
                objectFit: "cover",
                borderRadius: 12,
              }}
            />
          ) : (
            <Typography color="text.secondary">คลิกเพื่ออัปโหลดรูป</Typography>
          )}

          <input
            id="hospital-image"
            type="file"
            hidden
            accept="image/*"
            name="image"
            onChange={handleChange}
          />
        </Box>

        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="ชื่อโรงพยาบาล"
                name="name"
                value={hospital.name || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="ที่อยู่"
                name="address"
                value={hospital.address || ""}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="ตำบล/แขวง"
                name="subDistrict"
                value={hospital.subDistrict || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="อำเภอ/เขต"
                name="district"
                value={hospital.district || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="จังหวัด"
                name="province"
                value={hospital.province || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="เบอร์โทรศัพท์"
                name="phoneNumber"
                value={hospital.phoneNumber || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={saving}>
              {saving ? "กำลังบันทึก..." : "บันทึก"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
